import { Link } from "react-router-dom";

/**
 * The clickable surface of a story card.
 *
 * Every story-opening control on the homepage used to be a `<div onClick>`,
 * which meant no keyboard user and no screen-reader user could open a single
 * story — the card was invisible as a control and unreachable by Tab. That is
 * a WCAG 2.1 failure at Level A (2.1.1 Keyboard, 4.1.2 Name/Role/Value), and
 * on a product for six-year-olds it is the one accessibility bug that makes
 * everything else moot.
 *
 * So this is a real `<Link>`: Tab reaches it, Enter activates it, screen
 * readers announce it as a link to a story, and middle-click / cmd-click /
 * "open in new tab" / "copy link address" all behave the way people expect.
 *
 * A plain left-click is still intercepted and handed to `onActivate`, so
 * ordinary mouse browsing opens the on-page modal exactly as before and does
 * not lose its place in the page. Modified clicks are left entirely alone —
 * the browser should own those.
 *
 * Anything interactive (a bookmark button, an audio player) must be a SIBLING
 * of this element, never a child: interactive content nested inside an anchor
 * is invalid HTML and confuses assistive technology about what it is on.
 */
export const StoryLink = ({ to, onActivate, className = "", children, ...rest }) => {
  const handleClick = (event) => {
    // Modified and non-primary clicks belong to the browser: cmd/ctrl for a
    // new tab, shift for a new window, middle-click for a background tab.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    if (!onActivate) return; // no handler: fall through to a real navigation
    event.preventDefault();
    onActivate();
  };

  return (
    <Link to={to} onClick={handleClick} className={className} {...rest}>
      {children}
    </Link>
  );
};

export default StoryLink;
